/**
 * To map between short-form locale keys to full-form, e.g. 'fr' -> 'fr-FR'
 */
export const localeMap = {
  en: 'en',
  'en-GB': 'en-GB',
  'en-US': 'en-US',
  de: 'de-DE',
  cs: 'cs-CZ',
  ko: 'ko-KR',
  'pt-BR': 'pt-BR',
  fr: 'fr-FR',
  es: 'es',
  it: 'it-IT',
  ja: 'ja-JP',
  nl: 'nl-NL',
  nb: 'nb-NO',
  da: 'da-DK',
  sv: 'sv-SE',
  fi: 'fi-FI',
  ru: 'ru-RU',
  pl: 'pl-PL',
  tr: 'tr-TR',
  hu: 'hu-HU',
  'zh-CN': 'zh-CN',
  'zh-TW': 'zh-TW',
} as const;

/**
 * To map between a full locale tag to the short form, e.g. 'fr-FR' -> 'fr'
 * Reason for mapping: Confluence monolith provides the former, while Traduki provides the latter.
 * There is inconsistency in how each locale is formatted so the only surefire way is
 * to maintain this map.
 */
export const fullLocaleToShort = {
  'de-DE': 'de',
  'cs-CZ': 'cs',
  'ko-KR': 'ko',
  'fr-FR': 'fr',
  'it-IT': 'it',
  'ja-JP': 'ja',
  'nl-NL': 'nl',
  'nb-NO': 'nb',
  'da-DK': 'da',
  'sv-SE': 'sv',
  'fi-FI': 'fi',
  'ru-RU': 'ru',
  'pl-PL': 'pl',
  'tr-TR': 'tr',
  'hu-HU': 'hu',
  'en-GB': 'en-GB',
  'en-US': 'en-US',
  'pt-BR': 'pt-BR',
  'zh-CN': 'zh-CN',
  'zh-TW': 'zh-TW',
  'es-ES': 'es',
} as const;

// When adding a new locale, make sure to add it to this array
export const i18nLocales = [
  'en',
  'en-GB',
  'en-US',
  'de',
  'cs',
  'ko',
  // ATM pt-PT is deprecated but no decision has been made whether to swap users over to pt-BR,
  // so we'll fall back to en-US for it.
  // Nonetheless, we support pt-BR (brasilian portuguese) natively
  'pt-BR',
  'fr',
  'es',
  'it',
  'ja',
  'nl',
  'nb',
  'da',
  'sv',
  'fi',
  'ru',
  'pl',
  'tr',
  'hu',
  'zh-CN',
  'zh-TW',
] as const;

export const isValidLocale = (locale: unknown): locale is LongLocale => {
  return typeof locale === 'string' && Object.keys(fullLocaleToShort).includes(locale);
};

export type ShortLocale = (typeof i18nLocales)[number];
export type LongLocale = keyof typeof fullLocaleToShort;

export const debugLocales = [
  // Debug locales
  'en-pseudo',
  'xx',
  'en-ZZ',
];

export const supportedLocale = [...i18nLocales, ...debugLocales] as const;

export type SupportedLocale = (typeof supportedLocale)[number];

export const defaultLocale: SupportedLocale = 'en-US';

export const getSupportedLocale = (
  locale: string | undefined | null,
  strict = true
): SupportedLocale | null => {
  const defaultLocaleOrNull = strict ? null : defaultLocale;
  if (!locale) {
    return defaultLocaleOrNull;
  }
  try {
    // en-pseudo doesn't get parsed correctly by Intl.Locale, so we just handle all debug locales separately
    if (debugLocales.includes(locale)) {
      return locale as SupportedLocale;
    }
    const intlLocale = new Intl.Locale(locale);
    let longLocale: string;
    if (intlLocale.region) {
      longLocale = `${intlLocale.language}-${intlLocale.region}`;
    } else {
      longLocale = intlLocale.language;
    }
    if (longLocale === 'pt-PT') {
      // pt-PT is deprecated but no decision has been made whether to swap users over to pt-BR, so we'll fall back to
      // en-US for it.
      return defaultLocaleOrNull;
    }
    if (supportedLocale.includes(longLocale)) {
      return longLocale as SupportedLocale;
    }
    const shortLocale = intlLocale.language;
    if (shortLocale === 'zh') {
      // zh is ambiguous, so we default
      return defaultLocaleOrNull;
    }
    if (supportedLocale.includes(shortLocale)) {
      return shortLocale as SupportedLocale;
    }
    return defaultLocaleOrNull;
  } catch (error) {
    return defaultLocaleOrNull;
  }
};
